import { useState } from 'react';
import { useProgress } from '../hooks/useProgress';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useAuth } from '../hooks/useAuth';
import { ProgressStatsRow } from '../components/progress/ProgressStatsRow';
import { SimpleScoreChart, SkillsBreakdown } from '../components/progress/SimpleScoreChart';
import { SessionList } from '../components/progress/SessionList';
import { LeaderboardTabs } from '../components/progress/LeaderboardTabs';
import { HireAvailabilityCard } from '../components/progress/HireAvailabilityCard';
import clsx from 'clsx';

type ViewTab = 'stats' | 'leaderboard';


export function ProgressPage() {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<ViewTab>('stats');

  // Get progress data
  const { sessions, recentSessions, totals, isLoading: progressLoading, error: progressError } = useProgress();

  // Global leaderboard (server-side opt-in via public profile settings)
  const {
    entries: leaderboardEntries,
    currentUserRank,
    isOptedIn: isLeaderboardOptIn,
    isLoading: leaderboardLoading,
    error: leaderboardError,
  } = useLeaderboard();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Progress</h1>
          <p className="mt-1 text-gray-500">
            Track how your delivery improves over time
          </p>
        </div>

        {/* View tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveView('stats')}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors',
              activeView === 'stats'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Your Stats
          </button>
          <button
            onClick={() => setActiveView('leaderboard')}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors',
              activeView === 'leaderboard'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Leaderboard
          </button>
        </div>
      </div>

      {/* Loading / Error states */}
      {(progressLoading || leaderboardLoading) && (
        <div className="text-center py-12 text-gray-500">Loading your progress data...</div>
      )}
      {(progressError || leaderboardError) && (
        <div className="text-center py-8 text-red-600">
          Failed to load progress data. Please try again later.
        </div>
      )}

      {/* Stats View */}
      {activeView === 'stats' && !progressLoading && !progressError && (
        <div className="space-y-8">
          {/* Key stats row */}
          <ProgressStatsRow
            sessionsCount={totals.sessionsCount}
            avgClarity={totals.avgClarity}
            avgConfidence={totals.avgConfidence}
            bestStreak={totals.bestStreakDays}
            currentStreak={totals.currentStreakDays}
          />

          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column - Charts and sessions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Score trend chart */}
              <div className="card">
                <div className="card-header">
                  <h2 className="font-semibold text-gray-900">Score Trend</h2>
                  <p className="text-sm text-gray-500">Your recent session scores</p>
                </div>
                <div className="card-body">
                  <SimpleScoreChart sessions={recentSessions} height={220} average={totals.avgOverallScore} />
                </div>
              </div>

              {/* Recent sessions */}
              <div className="card">
                <div className="card-header flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900">Recent Sessions</h2>
                    <p className="text-sm text-gray-500">Your practice history</p>
                  </div>
                  {sessions.length > 0 && (
                    <span className="text-sm text-gray-400">
                      {sessions.length} total
                    </span>
                  )}
                </div>
                <SessionList sessions={sessions} maxItems={7} showHeader />
              </div>
            </div>

            {/* Right column - Skills and availability */}
            <div className="space-y-6">
              {/* Skills breakdown */}
              <div className="card">
                <div className="card-header">
                  <h2 className="font-semibold text-gray-900">Skills Breakdown</h2>
                </div>
                <div className="card-body">
                  <SkillsBreakdown
                    clarity={totals.avgClarity}
                    pace={totals.avgPace}
                    confidence={totals.avgConfidence}
                    energy={totals.avgEnergy}
                  />
                </div>
              </div>

              {/* Availability: the portfolio's real setting, not a local copy */}
              <HireAvailabilityCard />

              {/* Achievements */}
              <div className="card">
                <div className="card-header">
                  <h2 className="font-semibold text-gray-900">Achievements</h2>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-4 gap-2">
                    <AchievementBadge
                      name="First Steps"
                      emoji="🎯"
                      earned={totals.sessionsCount >= 1}
                    />
                    <AchievementBadge
                      name="Week Warrior"
                      emoji="🔥"
                      earned={totals.bestStreakDays >= 7}
                    />
                    <AchievementBadge
                      name="High Scorer"
                      emoji="🏆"
                      earned={totals.avgOverallScore !== null && totals.avgOverallScore >= 80}
                    />
                    <AchievementBadge
                      name="Dedicated"
                      emoji="⚡"
                      earned={totals.sessionsCount >= 10}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard View */}
      {activeView === 'leaderboard' && !leaderboardLoading && !leaderboardError && (
        <div className="space-y-6">
          <LeaderboardTabs
            entries={leaderboardEntries}
            currentUserId={user?.id}
            isOptedIn={isLeaderboardOptIn}
            currentUserRank={currentUserRank}
          />
        </div>
      )}

    </div>
  );
}

// Achievement badge component
function AchievementBadge({
  name,
  emoji,
  earned,
}: {
  name: string;
  emoji: string;
  earned: boolean;
}) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center p-3 rounded-lg transition-all',
        earned
          ? 'bg-primary-50'
          : 'bg-gray-100 opacity-50 grayscale'
      )}
      title={earned ? `${name} - Earned!` : `${name} - Locked`}
    >
      <div
        className={clsx(
          'w-10 h-10 rounded-full flex items-center justify-center text-xl',
          earned ? 'bg-primary-100' : 'bg-gray-200'
        )}
      >
        {emoji}
      </div>
      <span className="text-xs text-gray-600 mt-2 text-center leading-tight">
        {name}
      </span>
    </div>
  );
}
